import React, { useState } from 'react';
import {
    Button,
    Col,
    Divider,
    Input,
    Option,
    Row,
    Select,
    Text,
} from '@servahealth/serva-ui-kit';
import { CoreType, CoreDataPoint, CoreDataPointDataType } from '../Shared/CoreComponents';
import { AHRBDataPointFixedWhereConfiguration, AHRBDataPointFixedWhereOperator } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';

type WhereOption = {
    value: AHRBDataPointFixedWhereOperator;
    label: string;
};

export interface AHRBCoreDataPointFixedWhereProps {
    coreTypes: CoreType[];
    coreDataPoints: CoreDataPoint[];
    ahrbsDataPointID: number | undefined;
    selectedCoreDataPointColumnFixedWhere: AHRBDataPointFixedWhereConfiguration | undefined;
    onOk: (
        dataPointFixedWhere: AHRBDataPointFixedWhereConfiguration
    ) => void;
    onCancel: () => void;
}

export const AHRBCoreDataPointFixedWhere: React.FC<AHRBCoreDataPointFixedWhereProps> = ({ ...props }) => {

    const [selectedCoreDataPointFixedWhere, setSelectedCoreDataPointFixedWhere] = useState<AHRBDataPointFixedWhereOperator>(
        props.selectedCoreDataPointColumnFixedWhere?.where ?? ''
    );

    const [selectedCoreDataPointFixedWhereValueLHS, setSelectedCoreDataPointFixedWhereValueLHS] = useState<string>(
        props.selectedCoreDataPointColumnFixedWhere?.valueLHS ?? ''
    );

    const [selectedCoreDataPointFixedWhereValueRHS, setSelectedCoreDataPointFixedWhereValueRHS] = useState<string>(
        props.selectedCoreDataPointColumnFixedWhere?.valueRHS ?? ''
    );

    const [changeOccurred, setChangeOccurred] = useState(false);
    
    const selectedDataPoint = props.coreDataPoints.find(cdp => cdp.id === props.ahrbsDataPointID);
    const selectedFieldName = selectedDataPoint?.name;
    const selectedFieldTypeID = selectedDataPoint?.idType;
    const selectedFieldDataType = selectedDataPoint?.dataType;
    const selectedTypeName = props.coreTypes.find(ct => ct.id === selectedFieldTypeID)?.name;

    const handleWhereChange = (whereOperator: AHRBDataPointFixedWhereOperator) => {
        setChangeOccurred(true);
        setSelectedCoreDataPointFixedWhere(whereOperator);

        if (whereOperator === '' || whereOperator === 'ISNULL' || whereOperator === 'ISNOTNULL') {
            setSelectedCoreDataPointFixedWhereValueLHS('');
            setSelectedCoreDataPointFixedWhereValueRHS('');
        }
        else if (whereOperator !== 'BETWEEN') {
            setSelectedCoreDataPointFixedWhereValueRHS('');
        }
    };

    const handleWhereValueLHSChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChangeOccurred(true);
        setSelectedCoreDataPointFixedWhereValueLHS(e.target.value);
    };

    const handleWhereValueRHSChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChangeOccurred(true);
        setSelectedCoreDataPointFixedWhereValueRHS(e.target.value);
    };

    const handleOk = () => {
        const dataPointFixedWhere: AHRBDataPointFixedWhereConfiguration = {
            where: selectedCoreDataPointFixedWhere,
            valueLHS: selectedCoreDataPointFixedWhereValueLHS,
            valueRHS: selectedCoreDataPointFixedWhereValueRHS
        };

        props.onOk(dataPointFixedWhere);
    };
    
    const getAvailableWhereOptions = (dataType: CoreDataPointDataType | undefined): WhereOption[] => {
        switch (dataType) {
            case CoreDataPointDataType.Integer:
            case CoreDataPointDataType.Float:
            case CoreDataPointDataType.Autonumber:
                return [
                    { value: 'EQUALS', label: 'Equals' },
                    { value: 'NOTEQUALS', label: 'Not Equals' },
                    { value: 'GREATERTHAN', label: 'Greater Than' },
                    { value: 'GREATERTHANOREQUAL', label: 'Greater Than Or Equal' },
                    { value: 'LESSTHAN', label: 'Less Than' },
                    { value: 'LESSTHANOREQUAL', label: 'Less Than Or Equal' },
                    { value: 'BETWEEN', label: 'Between' },
                    { value: 'ISNULL', label: 'Is Null' },
                    { value: 'ISNOTNULL', label: 'Is Not Null' },
                ];

            case CoreDataPointDataType.Date:
            case CoreDataPointDataType.DateTime:
                return [
                    { value: 'EQUALS', label: 'Equals' },
                    { value: 'NOTEQUALS', label: 'Not Equals' },
                    { value: 'GREATERTHAN', label: 'Greater Than' },
                    { value: 'GREATERTHANOREQUAL', label: 'Greater Than Or Equal' },
                    { value: 'LESSTHAN', label: 'Less Than' },
                    { value: 'LESSTHANOREQUAL', label: 'Less Than Or Equal' },
                    { value: 'BETWEEN', label: 'Between' },
                    { value: 'ISNULL', label: 'Is Null' },
                    { value: 'ISNOTNULL', label: 'Is Not Null' },
                ];

            case CoreDataPointDataType.String:
            case CoreDataPointDataType.Phonenumber:
            case CoreDataPointDataType.Lookup:
                return [
                    { value: 'EQUALS', label: 'Equals' },
                    { value: 'NOTEQUALS', label: 'Not Equals' },
                    { value: 'CONTAINS', label: 'Contains' },
                    { value: 'STARTSWITH', label: 'Starts With' },
                    { value: 'ENDSWITH', label: 'Ends With' },
                    { value: 'ISNULL', label: 'Is Null' },
                    { value: 'ISNOTNULL', label: 'Is Not Null' },
                ];

            default:
                return [];
        }
    };

    const getWhereValuePlaceholder = (dataType: CoreDataPointDataType | undefined): string => {
        switch (dataType) {
            case CoreDataPointDataType.Integer:
            case CoreDataPointDataType.Float:
            case CoreDataPointDataType.Autonumber:
                return 'Enter number';

            case CoreDataPointDataType.Date:
            case CoreDataPointDataType.DateTime:
                return 'Enter date';

            case CoreDataPointDataType.String:
            case CoreDataPointDataType.Phonenumber:
            case CoreDataPointDataType.Lookup:
                return 'Enter text';

            default:
                return '';
        }
    };

    const availableWhereOptions = getAvailableWhereOptions(selectedFieldDataType);    
    
    const selectedWhereIsValid =
        selectedCoreDataPointFixedWhere.trim().length === 0 ||
        availableWhereOptions.some(option => option.value === selectedCoreDataPointFixedWhere);

    const hadExistingWhere = (props.selectedCoreDataPointColumnFixedWhere?.where?.trim().length ?? 0) > 0;

    const isRemovingWhere =
        hadExistingWhere &&
        changeOccurred &&
        selectedCoreDataPointFixedWhere.trim().length === 0;

    const selectedWhereRequiresNoValue =
        selectedCoreDataPointFixedWhere === 'ISNULL' ||
        selectedCoreDataPointFixedWhere === 'ISNOTNULL';

    const selectedWhereRequiresTwoValues =
        selectedCoreDataPointFixedWhere === 'BETWEEN';

    const selectedWhereRequiresOneValue =
        selectedCoreDataPointFixedWhere.trim().length > 0 &&
        !selectedWhereRequiresNoValue &&
        !selectedWhereRequiresTwoValues;

    const valueLHSHasValue =
        selectedCoreDataPointFixedWhereValueLHS.trim().length > 0;

    const valueRHSHasValue =
        selectedCoreDataPointFixedWhereValueRHS.trim().length > 0;

    const whereValuePlaceholder = getWhereValuePlaceholder(selectedFieldDataType);        

    const whereValueMatchesDataType = (
        value: string,
        dataType: CoreDataPointDataType | undefined
    ): boolean => {
        if (value.trim().length === 0) {
            return false;
        }

        switch (dataType) {
            case CoreDataPointDataType.Integer:
            case CoreDataPointDataType.Autonumber:
                return Number.isInteger(Number(value));

            case CoreDataPointDataType.Float:
                return !Number.isNaN(Number(value));

            case CoreDataPointDataType.Date:
            case CoreDataPointDataType.DateTime:
                return !Number.isNaN(Date.parse(value));

            case CoreDataPointDataType.String:
            case CoreDataPointDataType.Phonenumber:
            case CoreDataPointDataType.Lookup:
                return true;

            default:
                return false;
        }
    };

    const valueLHSMatchesDataType =
        whereValueMatchesDataType(selectedCoreDataPointFixedWhereValueLHS, selectedFieldDataType);

    const valueRHSMatchesDataType =
        whereValueMatchesDataType(selectedCoreDataPointFixedWhereValueRHS, selectedFieldDataType);

    const valueLHSDisabled =
        selectedCoreDataPointFixedWhere.trim().length === 0 ||
        selectedWhereRequiresNoValue;

    const valueRHSDisabled = !selectedWhereRequiresTwoValues;

    const hasValidWhereConfiguration =
        (
            selectedCoreDataPointFixedWhere.trim().length > 0 &&
            selectedWhereIsValid &&
            (
                selectedWhereRequiresNoValue ||
                (
                    selectedWhereRequiresOneValue &&
                    valueLHSHasValue &&
                    valueLHSMatchesDataType
                ) ||
                (
                    selectedWhereRequiresTwoValues &&
                    valueLHSHasValue &&
                    valueRHSHasValue &&
                    valueLHSMatchesDataType &&
                    valueRHSMatchesDataType
                )
            )
        ) ||
        isRemovingWhere;

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 4,
        height: '95%'
    };

    return (
        <>
            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={4} />
                <Col span={16}>
                    <div style={contentStyle}>
                        <h3 className='text-2xl pt-2 px-2'><u>Fixed Where</u></h3>
                        <h5 className='text-2xl pt-2 px-2'><i>Embedded In Backend Query</i></h5>
                    </div>
                </Col>
                <Col span={4} />
            </Row>
            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} style={{ backgroundColor: 'palegreen', borderRadius: '8px', padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={1} />
                <Col span={11}>
                    <Text italic strong>Field:</Text>{" "}
                    <Text>{selectedFieldName}</Text>
                </Col>
                <Col span={11}>
                    <Text italic strong>Type:</Text>{" "}
                    <Text>{selectedTypeName}</Text>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col span={1} />
                <Col span={3}>
                    <div style={{ padding: '4px', textAlign: 'right' }}>
                        <Text>Where:</Text>
                    </div>
                </Col>
                <Col span={5}>
                    <Select
                        showSearch
                        style={{ width: '100%' }}
                        value={selectedCoreDataPointFixedWhere}
                        onChange={handleWhereChange}
                    >
                        <Option value={''} label={'(Not Set)'}>
                            {'(Not Set)'}
                        </Option>
                        {
                            availableWhereOptions.map(whereOption => (
                                <Option
                                    key={whereOption.value}
                                    value={whereOption.value}
                                    label={whereOption.label}
                                >
                                    {whereOption.label}
                                </Option>
                            ))
                        }
                    </Select>
                </Col>
                <Col span={3}>
                    <div style={{ padding: '4px', textAlign: 'right' }}>
                        <Text>Value:</Text>
                    </div>
                </Col>
                <Col span={4}>
                    <Input
                        allowClear={true}
                        disabled={valueLHSDisabled}
                        placeholder={whereValuePlaceholder}
                        value={selectedCoreDataPointFixedWhereValueLHS}
                        onChange={handleWhereValueLHSChange}
                    />
                </Col>
                <Col span={2}>
                    <div style={{ padding: '4px', textAlign: 'center' }}>
                        <Text disabled={!selectedWhereRequiresTwoValues}>And</Text>
                    </div>
                </Col>
                <Col span={4}>
                    <Input
                        allowClear={true}
                        disabled={valueRHSDisabled}
                        placeholder={whereValuePlaceholder}
                        value={selectedCoreDataPointFixedWhereValueRHS}
                        onChange={handleWhereValueRHSChange}
                    />
                </Col>
                <Col span={2} />
            </Row>
            <Row gutter={16} style={{ padding: '16px', paddingBottom: '4px', paddingTop: '4px' }}>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} style={{ padding: '16px', paddingTop: '2px', paddingBottom: '2px' }}>
                <Col span={15} />
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            disabled={!changeOccurred || !hasValidWhereConfiguration}
                            htmlType="button"
                            style={{width: '95%'}}
                            type='primary'
                            onClick={handleOk}
                        >
                            OK
                        </Button>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                            <Button
                                style={{width: '95%'}}
                                type="default"
                                onClick={() => props.onCancel()}
                            >
                                Cancel
                            </Button>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
        </>
    );
};

export default AHRBCoreDataPointFixedWhere;